import type { JSX } from 'react';
import { useTranslation } from '../i18n';
import { guideUrl, type GuideAnchor } from '../help/guides';
import { InfoNote } from './InfoNote';

/**
 * Справка там, где решение неочевидно (§11, фаза 10, задача 11).
 *
 * Отличается от `InfoNote` ровно одним: под текстом стоит ссылка
 * в руководство. Пояснение в интерфейсе отвечает на вопрос «что выбрать
 * прямо сейчас» тремя строками; всё остальное — таблица способов доступа
 * к архиву, разбор порогов, устройство слоёв — живёт в руководстве,
 * и переписывать его во всплывающую панель незачем.
 *
 * Текст берётся из словаря обычными ключами (`help.*`) и переводится
 * наравне с остальным интерфейсом: справка, оставшаяся русской при
 * английском языке, — это отдельный критерий приёмки фазы.
 */
export function HelpNote({
  topic,
  anchor,
  summary,
}: {
  /** Ключ раздела справки: `help.<topic>.label` и `help.<topic>.text`. */
  topic: string;
  /** Куда ведёт ссылка «подробнее»: файл руководства и якорь раздела. */
  anchor: GuideAnchor;
  /** Короткая подпись рядом со значком — там, где само наличие важно. */
  summary?: string;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <InfoNote label={t(`help.${topic}.label`)} summary={summary}>
      <p className="leading-snug">{t(`help.${topic}.text`)}</p>

      <a
        href={guideUrl(anchor)}
        target="_blank"
        rel="noreferrer"
        className="mt-1.5 inline-block text-info hover:underline"
      >
        {t('help.more')}
      </a>
    </InfoNote>
  );
}
